import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Box, Typography, Container, useTheme, Alert, Link } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useSelector, useDispatch } from "react-redux";
import {
  selectStrategies,
  selectActiveRequests,
} from "../../Redux/Reducers/selectors";
import {
  selectHistoricalSignalSet,
  setHistoricalSignalSet,
} from "../../Redux/Reducers/strategiesSlice";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import {
  addActiveRequests,
  removeActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import {
  getDateRange,
  processNavSeriesData,
  calculateYAxisDomain,
  calculateLogMin,
} from "../../utils/strategyPerformanceUtils";
import { calculatePerformanceTableData } from "../../utils/calculatePerformanceTableData";
import { toast } from "react-toastify";
import StrategyPerformanceFilters from "./StrategyPerformanceFilters";
import StrategyPerformanceMetrics from "./StrategyPerformanceMetrics";
import StrategyPerformanceChart from "./StrategyPerformanceChart";
import StrategyPerformanceTable from "./StrategyPerformanceTable";
import SignalSetHoldings from "./SignalSetHoldings";
import { useStrategyMetrics } from "./useStrategyMetrics";

const StrategyPerformance = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const strategies = useSelector(selectStrategies);
  const historicalSignalSet = useSelector(selectHistoricalSignalSet);
  const activeRequests = useSelector(selectActiveRequests);
  const [strategyVal, setStrategyVal] = useState("");
  const [strategyValObj, setStrategyValObj] = useState(null);
  const [yScale, setYScale] = useState("linear");
  const [benchmark, setBenchmark] = useState("QQQ");
  const [period, setPeriod] = useState("Inception to date");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  // Applied dates (used for filtering) - only updated when Submit is clicked
  const [appliedCustomFromDate, setAppliedCustomFromDate] = useState("");
  const [appliedCustomToDate, setAppliedCustomToDate] = useState("");
  const timeoutRef = useRef(null);
  const activeRequestsRef = useRef([]);

  // Benchmark mapping: value -> display name
  const benchmarkNames = {
    QQQ: "Nasdaq 100",
    SPY: "S&P 500",
    IWM: "Russell 2000",
    IWV: "Russell 3000",
  };

  const strategyList = strategies
    ?.map((i) => i?.name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  useEffect(() => {
    activeRequestsRef.current = activeRequests;
  }, [activeRequests]);

  // Function to fetch historical data
  const handleFetchHistorical = useCallback(async () => {
    if (!strategyValObj) {
      return;
    }

    try {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Clear old data first
      dispatch(setHistoricalSignalSet([]));
      dispatch(addActiveRequests("get_historical_signal_set"));

      const requestData = {
        action: "get_historical_signal_set",
        data: {
          strategy: strategyValObj,
          fetchPerformance: true,
          fetchBacktestSummary: true,
        },
      };

      sendWebSocketMessage(JSON.stringify(requestData));

      timeoutRef.current = setTimeout(() => {
        const stillFetching = activeRequestsRef.current.includes(
          "get_historical_signal_set"
        );
        if (stillFetching) {
          dispatch(setHistoricalSignalSet([]));
          dispatch(removeActiveRequests("get_historical_signal_set"));
          toast.error("Unable to fetch historical signals");
        }
        timeoutRef.current = null;
      }, 120000); // 120 seconds = 120000ms
    } catch (error) {
      console.error("Error sending websocket request:", error);
      dispatch(removeActiveRequests("get_historical_signal_set"));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [strategyValObj, strategyVal, dispatch]);

  // Handle strategy selection change
  useEffect(() => {
    if (strategyVal) {
      // Find the strategy object by name to get the ID
      const selectedStrategy = strategies?.find((s) => s?.name === strategyVal);
      if (selectedStrategy?.strategyId) {
        setStrategyValObj(selectedStrategy.strategyId);
      }
    } else {
      setStrategyValObj(null);
      // Clear historical signals when strategy is cleared
      dispatch(setHistoricalSignalSet([]));
      dispatch(removeActiveRequests("get_historical_signal_set"));
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [strategyVal, strategies, dispatch]);

  // Fetch historical data when strategy is selected (only when strategyValObj changes from null to a value)
  const prevStrategyValObjRef = useRef(null);
  useEffect(() => {
    // Only fetch if strategyValObj is set AND it's a new selection (not a deselection)
    if (strategyValObj && strategyValObj !== prevStrategyValObjRef.current) {
      handleFetchHistorical();
      prevStrategyValObjRef.current = strategyValObj;
    } else if (!strategyValObj) {
      // Reset ref when strategy is cleared
      prevStrategyValObjRef.current = null;
    }

    // Cleanup timeout on unmount or when strategy changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [strategyValObj, handleFetchHistorical]);

  // Get min and max dates from strategyNavSeries for date input restrictions
  const dateRangeLimits = useMemo(() => {
    if (!historicalSignalSet?.navs?.strategyNavSeries) return null;
    const dates = Object.keys(historicalSignalSet.navs.strategyNavSeries).sort(
      (a, b) => a.localeCompare(b)
    );
    if (!dates.length) return null;
    return {
      minDate: dates[0],
      maxDate: dates[dates.length - 1],
    };
  }, [historicalSignalSet]);

  // Process NAV series data
  const chartData = useMemo(() => {
    if (!strategyVal || !historicalSignalSet?.navs?.strategyNavSeries)
      return [];

    // Use applied dates for Custom period, input dates for other periods
    const fromDate = period === "Custom" ? appliedCustomFromDate : customFromDate;
    const toDate = period === "Custom" ? appliedCustomToDate : customToDate;
    const dateRange = getDateRange(period, fromDate, toDate);
    const data = processNavSeriesData(
      historicalSignalSet,
      benchmark,
      dateRange
    );

    
    return data;
  }, [
    strategyVal,
    historicalSignalSet,
    benchmark,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
  ]);

  // Use custom hook for metrics calculation
  // Note: Metrics always use strategyNavSeries, benchmark is only for chart display
  const { metricData } = useStrategyMetrics({
    strategyVal,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
    chartData,
    dateRangeLimits,
  });

  // If strategy is cleared, force back to arithmetic (linear) to avoid log scale with zeros
  useEffect(() => {
    if (!strategyVal && yScale !== "linear") setYScale("linear");
  }, [strategyVal, yScale]);

  // Reset applied dates when period changes away from Custom
  useEffect(() => {
    if (period !== "Custom") {
      setAppliedCustomFromDate("");
      setAppliedCustomToDate("");
    }
  }, [period]);

  const logMin = useMemo(() => calculateLogMin(chartData), [chartData]);
  const yAxisDomain = useMemo(
    () => calculateYAxisDomain(chartData, yScale, logMin),
    [chartData, yScale, logMin]
  );

  // Calculate performance table data from original strategyNavSeries (independent of period filters)
  const performanceData = useMemo(() => {
    if (!historicalSignalSet?.navs?.strategyNavSeries) {
      return [];
    }
    // Use original strategyNavSeries (not rebased) for table data
    return calculatePerformanceTableData(historicalSignalSet.navs.strategyNavSeries);
  }, [historicalSignalSet]);
  return (
    <>
      <Helmet>
        <title>Strategy Performance | Vantage Alpha</title>
      </Helmet>

      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === "light" ? "#ebebeb" : theme.palette.grey[50],
          background:
            theme.palette.mode === "light" ? "#ebebeb" : theme.palette.grey[50],
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          {/* Page Title */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
            >
              Strategy Performance
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Comprehensive backtest performance analysis by strategy
            </Typography>
          </Box>

          {/* Beta Disclaimer - full width */}
          <Alert
            severity="warning"
            icon={false}
            sx={{ mb: 3, width: "100%", "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              🚧 Beta Feature Notice
            </Typography>
            <Typography variant="body2" component="span" sx={{ display: "block" }}>
              Strategy Performance is still in beta and may contain errors or incomplete data.
              Please avoid making financial decisions based on this information alone.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              If you spot a bug or something that doesn&apos;t look right, help us improve by reporting it at:{" "}
<Link href="mailto:support@vantagealpha.com" color="inherit" sx={{ fontWeight: 600 }}>
                  support@vantagealpha.com
                </Link>
            </Typography>
          </Alert>

          {/* Strategy and Period Filters */}
          <StrategyPerformanceFilters
            strategyList={strategyList}
            strategyVal={strategyVal}
            setStrategyVal={setStrategyVal}
            period={period}
            setPeriod={setPeriod}
            customFromDate={customFromDate}
            setCustomFromDate={setCustomFromDate}
            customToDate={customToDate}
            setCustomToDate={setCustomToDate}
            appliedCustomFromDate={appliedCustomFromDate}
            setAppliedCustomFromDate={setAppliedCustomFromDate}
            appliedCustomToDate={appliedCustomToDate}
            setAppliedCustomToDate={setAppliedCustomToDate}
            dateRangeLimits={dateRangeLimits}
          />

          {/* Metric Cards */}
          <StrategyPerformanceMetrics metricData={metricData} />

          {/* Chart Section */}
          <Box sx={{ mb: 3 }}>
            <StrategyPerformanceChart
              strategyVal={strategyVal}
              chartData={chartData}
              benchmark={benchmark}
              setBenchmark={setBenchmark}
              benchmarkNames={benchmarkNames}
              yScale={yScale}
              setYScale={setYScale}
              yAxisDomain={yAxisDomain}
              activeRequests={activeRequests}
            />
          </Box>

          {/* Monthly & Annual Performance Table */}
          <StrategyPerformanceTable performanceData={performanceData} />

          {/* Signal set holdings table  */}
          {/* <SignalSetHoldings /> */}

        </Container>
      </Box>
    </>
  );
};

export default StrategyPerformance;
